<template>
  <SloCard
    class="spec-card"
    target="_blank"
    :url="product?.disabled ? false : url"
    :body-link="!product?.disabled"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-slot:overlay>
      <div class="overlay">
        <SloButton v-if="!readonly" fill="clear" @click="handleDelete()">
          <SloIcon name="Trash" />
        </SloButton>
      </div>
    </template>
    <template v-slot:image>
      <img
        class="product-image"
        :src="product?.image ? product.image : '/themes/sloan/img/product-placeholder.png'"
        :alt="product?.title"
      />
    </template>
    <template v-slot:title v-if="product?.disabled">
      {{ 'Product Discontinued' | t }}
    </template>
    <template v-slot:title v-else>
      {{ product?.title }}
    </template>
    <p
      class="product-description"
      v-if="product && product?.description"
      v-html="product?.description"
    />
    <p
      class="product-sku"
      v-if="
        product &&
        product?.sku &&
        (product?.product_type !== 'sink' || product?.product_line === 'Mobile Handwashing Station')
      "
      v-html="'Code ' + product?.sku"
    />
  </SloCard>
</template>

<script>
import t from '../../main/i18n';
import {mapActions, mapGetters} from 'vuex';
import SloCard from '../../../lib/components/SloCard.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';

export default {
  name: 'SpecCard',
  components: {
    SloCard,
    SloButton,
    SloIcon
  },
  filters: {
    t
  },
  props: {
    readonly: {type: Boolean, default: false},
    product: {type: Object, required: true},
    section: {type: Object, required: true},
    project: {type: Object, required: true}
  },
  data() {
    return {
      _drag: false
    };
  },
  computed: {
    ...mapGetters('designspec', ['selectIndex']),
    url() {
      if (!this.product) return null;
      return this.product.url;
    },
    isDragging() {
      return (this.project.meta || {}).isDragging;
    },
    drag: {
      get() {
        if (this._drag) return this._drag;
        return this.isDragging;
      },
      set(drag) {
        this._drag = drag;
      }
    },
    checked: {
      get() {
        return this.$store.getters['designspec/isChecked'](
          this.project.id,
          `${this.section.id}:${this.product.id}`
        );
      },
      set() {
        this.$store.dispatch('designspec/toggleProductChecked', {
          project: this.project.id,
          section: this.section.id,
          product: this.product.id
        });
      }
    }
  },
  methods: {
    ...mapActions('designspec', ['removeProduct']),
    handleDelete() {
      this.removeProduct({
        project: this.project.id,
        section: this.section.id,
        product: this.product.id
      });
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/system';

.slo-card.spec-card {
  .slo-card--content {
    padding: 2rem;
  }
}
</style>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.spec-card {
  flex: 1;
  min-width: 192px;
  min-height: 256px;
  border: 1px solid transparent;
  cursor: grab;

  @supports (display: grid) {
    width: 100%;
  }

  .overlay {
    opacity: 0;
    transition: opacity 250ms cubic-bezier(0.165, 0.84, 0.44, 1);
  }

  &:hover,
  &:focus,
  &:focus-within {
    .overlay {
      pointer-events: initial;
      opacity: 1;
    }
  }

  &:active {
    cursor: grabbing;
  }
}

.spec-card:hover .overlay {
  opacity: 1;
}

.overlay {
  margin-top: -10px;
  margin-right: -10px;
  background: transparent;
  padding: 10px;

  .slo-button {
    padding: 10px;
    border-radius: 999px;
  }
}

.spec-card.drag {
  transition: transform 0s;
}

.spec-card.selected {
  border-color: $sloan-blue;
}

.spec-card.ghost {
  box-sizing: border-box;
  background: $color-gray-100;
  cursor: grabbing;

  *,
  &::after,
  *::before,
  *::after {
    visibility: hidden;
    background: transparent;
  }
}

.product-image {
  max-width: 256px;
  user-select: none;
  pointer-events: none;
}

.product-description {
  @include text('sm');
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-sku {
  @include text('sm');
  color: $color-gray-700;
  margin-top: 1em;
}
</style>
